import { useEffect, useState } from "react";
import { message } from "antd";
import { useGetInventoryQuery } from "../../../api/inventory/inventoryApi";
import { InventoryItem } from "../../../api/inventory/InventoryItem";
import { useGetDataMappingsQuery } from "../../../api/dataMapping/dataMappingApi";
import { useGetReservedQuery, usePostReservedMutation } from "../../../api/redemption/redemptionApi";
import { Redemption } from "../../../api/redemption/Redemption";

export const VINAPHONE_SLUG = "-vnvina-";
export const VINAMOBI_SLUG = "-vnmobi-";
export const TMOB_SLUG = "-tmob-";
export const TMOB2_SLUG = "tmobile";

export type VinaPluginInput = {
  orderId: string;
  simIdx: number;
  origIccid: string;
  custEmail: string;
  empName: string;
  qrUrl: string;
  iccid: string;
  smdp: string;
  activationCode: string;
  confirmationCode: string;
}

/**
 * Auto-selects a Vina esim, that is currently not assigned.
 */
export default function useVina(
  orderId?: string,
  simIdx?: number,
  sku?: string,
  cancelFlag?: boolean,
  dt?: string,
  redemption?: Redemption,
  createdAt?: string)
{
  const { data } = useGetInventoryQuery();
  const vendorId = sku?.split("-")[0] ?? "we";
  const { data: dataMapping, isFetching: dmIsFetch } = useGetDataMappingsQuery({
    vendorId: vendorId,
  });

  const { data: reservedSim, isFetching: rIsFetch, error: rErr} = useGetReservedQuery({
    orderId: orderId ?? "",
    simIdx: simIdx ?? 0
  }, { skip: !orderId });

  const [sel, setSel] = useState<InventoryItem>();
  const [postReservationSim] = usePostReservedMutation();
  const [isAutomatedVina, setIsAutomatedVina] = useState(false);

  const [isJustReserved, setIsJustReserved] = useState(false);

  useEffect(() => {
    async function run() {
    setSel(undefined);
    if (sku === undefined
      || (!sku.includes(VINAPHONE_SLUG)
          && !sku.includes(VINAMOBI_SLUG)
          && !sku.includes(TMOB_SLUG)
          && !sku.includes(TMOB2_SLUG))
      || !data
    ) {
      return;
    }
    if (!dataMapping || dmIsFetch) {
      return;
    }
    if (cancelFlag) {
      console.log("vina/tmob plugin: not setting for canceled", cancelFlag);
      setSel(undefined);
      return;
    }
    if (redemption && redemption.AssignedSimId != null) {
      console.log("vina/tmob plugin: not setting for already assigned sim", redemption);
      setSel(undefined);
      return;
    }
    if (orderId == null || simIdx == null) {
      console.log("missing order id, sim idx");
      setSel(undefined);
      return;
    }

    if (reservedSim != null && reservedSim.ReservedOrderId === orderId && reservedSim.ReservedSimIdx === simIdx) {
      console.log("vina/tmob plugin: rserved sim alr avail", reservedSim);
      message.success("Loaded reserved eSIM for #" + simIdx + " with ICCID " + reservedSim.Iccid);
      setSel(reservedSim);
      return;
    }

    console.log("vina/tmob plugin: 74 attempting to select esim..");
    const vmap = dataMapping.Items.find(x => x.InternalSku === sku);
    if (!vmap) {
      message.error("No mapping found for SIMPLY SKU " + sku);
      console.log("No mapping found for SIMPLY SKU", sku, dataMapping);
      return;
    }
    if (!rErr) {
      console.log("vina/tmob plugin: waiting for error before proceeding");
      return;
    }
    const vsku = vmap.VendorSku;  // provider code

    const isOrderPlacedBeforeChangeMapping = createdAt
      ? (createdAt < "2025-09-05T06:20")
      : false;
    if (vsku.includes("-vn-vinaphone") && !isOrderPlacedBeforeChangeMapping) {
      console.log("vina/tmob plugin: not selecting for automated vinaphone sku", vsku);
      // setIsAutomatedVina(true);
      // return;
    }
    setIsAutomatedVina(false);
    const vinaSims = data.filter(x => x.VendorSku === vsku);
    console.log("vina/tmob plugin: all esims", data);
    console.log("vina/tmob plugin: vendor-specific vina/tmob sims", vinaSims);
    const unused = vinaSims.filter(x =>
      (x.RedemptionOrderId == null || x.RedemptionOrderId?.length === 0)
      && (x.ReservedOrderId == null && x.ReservedSimIdx == null)
      && (x.ExpiryDate != null
          ? x.ExpiryDate > new Date().toISOString().split("T")[0]
          : true)
    );
    console.log("vina/tmob plugin: unused", unused, new Date().toISOString().split("T")[0]);
    unused.sort((a, b) => a.ExpiryDate?.localeCompare(b.ExpiryDate));
    if (unused.length === 0) {
      message.error("Vinaphone/tmob eSIMs for provider code " + vsku + " out of stock.");
      console.log("vina/tmob plugin: ", data);
      return;
    }
    const earliestExp = unused[0];
    const earliestExps = unused.filter(x =>
        x.ExpiryDate === earliestExp.ExpiryDate);
    const idx = Math.floor(Math.random() * earliestExps.length);
    const sim = earliestExps[idx];
    console.log("vina/tmob plugin: selected vinaphone/tmob esim for vsku " + vsku, sim);

    console.log("vina/tmob plugin: found esim to reserve", sim);
      try {
        const reserv = await postReservationSim({
          orderId: orderId,
          simIdx: simIdx,
          iccid: sim["Iccid"]
        }).unwrap();
        console.log("vina/tmob plugin: Successfully reserved sim", reserv);
        if (reserv.ConfirmationCode) {
          message.success("Reserved eSIM with confirmation code " + reserv.ConfirmationCode);
        } else {
          message.success("Reserved eSIM with ICCID " + reserv.Iccid);
        }
        setSel(sim);
        setIsJustReserved(true);
      } catch (ex: any) {
        console.error("vina/tmob plugin: error reserving esim:", ex);

        if (ex.data?.message === "A reserved esim already exists") {
          return;
        }
        message.error("Error reserving eSIM");


      }
    }
    run();
  }, [data, sku, dataMapping, dmIsFetch, rIsFetch, reservedSim, orderId, postReservationSim, simIdx, cancelFlag, rErr, createdAt]);

  return [sel, isAutomatedVina, isJustReserved];
}